import { getRoutePoints, cumulativeKmAtRoutePoints, type RoutePoint } from "@/lib/segment-utils"
import { pathTotalKm, buildPathFromRide } from "@/lib/route-matching"
import { ceilMinutesTo5 } from "@/lib/utils"
import { APP_TIMEZONE, shiftYmd, utcInstantToZonedParts, zonedDayBoundsUtc, zonedWallClockToUtc } from "@/lib/timezone"

export function parseDurationToMinutes(duration: string): number {
  const hourMatch = duration?.match(/(\d+)\s*h/)
  const minMatch = duration?.match(/(\d+)\s*min/)
  const hours = hourMatch ? parseInt(hourMatch[1]) : 0
  const mins = minMatch ? parseInt(minMatch[1]) : 0
  return hours * 60 + mins
}

export function rideStartDate(ride: any): Date | null {
  if (!ride?.date || !ride?.departureTime) return null
  return zonedWallClockToUtc(ride.date, ride.departureTime, APP_TIMEZONE)
}

export function rideArrivalDate(ride: any): Date | null {
  const start = rideStartDate(ride)
  if (!start) return null
  const totalMinutes = parseDurationToMinutes(ride?.route?.duration || "")
  if (totalMinutes <= 0) return null
  return new Date(start.getTime() + totalMinutes * 60000)
}

export interface PointTime {
  index: number
  name: string
  date: Date | null
}

export function computePointTimes(ride: any): PointTime[] {
  const points = getRoutePoints(ride)
  const start = rideStartDate(ride)
  if (!start) return points.map((p) => ({ index: p.index, name: p.name, date: null }))

  const totalMinutes = parseDurationToMinutes(ride?.route?.duration || "")
  const alongKm = cumulativeKmAtRoutePoints(ride, points)
  const totalKm = alongKm.length > 0 ? alongKm[alongKm.length - 1] : 0

  const times: PointTime[] = points.map((p: RoutePoint, i: number) => {
    let date: Date | null = null
    if (i === 0) {
      date = new Date(start.getTime())
    } else if (totalKm > 0 && totalMinutes > 0) {
      const fraction = (alongKm[i] || 0) / totalKm
      date = new Date(start.getTime() + totalMinutes * 60000 * fraction)
    } else {
      date = new Date(start.getTime() + totalMinutes * 60000)
    }
    return { index: p.index, name: p.name, date }
  })

  const ordered = [...times].sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0))
  for (let i = 1; i < ordered.length; i++) {
    const prev = ordered[i - 1]
    const cur = ordered[i]
    if (cur.date && prev.date && cur.date < prev.date) {
      cur.date = new Date(prev.date.getTime())
    }
  }

  return points.map((p: RoutePoint) => times.find((t) => t.index === p.index) || {
    index: p.index,
    name: p.name,
    date: null,
  })
}

export function trechoDepartureDate(ride: any, boardStopIndex: number): Date | null {
  const times = computePointTimes(ride)
  const t = times.find((x) => x.index === boardStopIndex)
  return t?.date || null
}

export function trechoDepartureDateFromTime(ride: any, timeStr: string | null | undefined): Date | null {
  if (!timeStr) return null
  const start = rideStartDate(ride)
  if (!start) return null
  const d = zonedWallClockToUtc(ride?.date, timeStr, APP_TIMEZONE)
  if (!d) return null
  if (d.getTime() < start.getTime()) {
    const parts = utcInstantToZonedParts(d, APP_TIMEZONE)
    const nextDay = shiftYmd(`${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`, 1)
    return nextDay ? zonedWallClockToUtc(nextDay, timeStr, APP_TIMEZONE) : null
  }
  return d
}

export function isTrechoExpired(trechoDepartureDateTime: Date | null, now: Date, graceMinutes = 5): boolean {
  if (!trechoDepartureDateTime) return false
  return trechoDepartureDateTime.getTime() < now.getTime() - graceMinutes * 60 * 1000
}

export function isTrechoOnDay(dateStr: string, trechoDepartureDateTime: Date | null): boolean {
  if (!trechoDepartureDateTime) return true
  const bounds = zonedDayBoundsUtc(dateStr, APP_TIMEZONE)
  if (!bounds) return true
  const t = trechoDepartureDateTime.getTime()
  return t >= bounds.start.getTime() && t < bounds.end.getTime()
}

export function trechoArrivalDate(ride: any, alightStopIndex: number): Date | null {
  const times = computePointTimes(ride)
  const t = times.find((x) => x.index === alightStopIndex)
  return t?.date || null
}

export function trechoTimeAtKm(ride: any, km: number): Date | null {
  const start = rideStartDate(ride)
  if (!start) return null
  const totalKm = pathTotalKm(buildPathFromRide(ride), ride?.route?.totalKm)
  const totalMinutes = parseDurationToMinutes(ride?.route?.duration || "")
  if (totalKm <= 0 || totalMinutes <= 0) return new Date(start.getTime())
  return new Date(start.getTime() + Math.round((km / totalKm) * totalMinutes) * 60000)
}

export function trechoArrivalDateFromTime(ride: any, timeStr: string | null | undefined): Date | null {
  return trechoDepartureDateFromTime(ride, timeStr)
}

export function formatTime(date: Date | null | undefined, options: { round?: boolean } = {}): string {
  if (!date || isNaN(date.getTime())) return ""
  const { round = true } = options
  const p = utcInstantToZonedParts(date, APP_TIMEZONE)
  const total = round ? ceilMinutesTo5(p.hour * 60 + p.minute) : p.hour * 60 + p.minute
  const hh = Math.floor(total / 60) % 24
  const mm = total % 60
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`
}

export function estimateKmToPoints(ride: any): number[] {
  const points = getRoutePoints(ride)
  return cumulativeKmAtRoutePoints(ride, points)
}

export function totalRouteKm(ride: any): number {
  const points = getRoutePoints(ride)
  const along = cumulativeKmAtRoutePoints(ride, points)
  return along.length > 0 ? along[along.length - 1] : pathTotalKm(getRoutePoints(ride).map((p) => ({ lat: p.lat, lng: p.lng })))
}
